import {
  cloneElement,
  isValidElement,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';

import { cn } from '@/lib/cn';

export interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
  className?: string;
}

/**
 * Подпись + контрол + подсказка/ошибка. Раунд 4, N8: раньше всё лежало внутри `<label>`, и имя
 * поля читалось склейкой «Год рождения1966–2008». Теперь подпись — `<label htmlFor>`, а подсказка
 * и ошибка привязаны к контролу через `aria-describedby`.
 */
export function Field({ label, hint, error, children, className }: FieldProps) {
  const id = useId();
  const noteId = `${id}-note`;
  const note = error ?? hint;
  const control = isValidElement<{ id?: string; 'aria-describedby'?: string }>(children)
    ? cloneElement(children, {
        id: children.props.id ?? id,
        'aria-describedby': note ? noteId : children.props['aria-describedby'],
      })
    : children;
  const controlId = isValidElement<{ id?: string }>(children) ? (children.props.id ?? id) : undefined;
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label ? (
        <label
          htmlFor={controlId}
          className="text-muted text-xs font-semibold tracking-wide uppercase"
        >
          {label}
        </label>
      ) : null}
      {control}
      {error ? (
        <span id={noteId} className="text-danger text-sm">
          {error}
        </span>
      ) : hint ? (
        <span id={noteId} className="text-muted text-sm">
          {hint}
        </span>
      ) : null}
    </div>
  );
}

const controlClass =
  'rounded-control bg-surface-2 text-fg placeholder:text-muted/70 w-full border border-border px-4 text-base outline-none focus:border-accent';

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export function TextInput({ className, invalid, ...rest }: TextInputProps) {
  return (
    <input
      className={cn(controlClass, 'h-12', invalid && 'border-danger', className)}
      aria-invalid={invalid || undefined}
      {...rest}
    />
  );
}

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export function TextArea({ className, invalid, ...rest }: TextAreaProps) {
  return (
    <textarea
      className={cn(controlClass, 'min-h-24 py-3', invalid && 'border-danger', className)}
      aria-invalid={invalid || undefined}
      {...rest}
    />
  );
}

export interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  hint?: ReactNode;
  disabled?: boolean;
}

/** Переключатель-строка: чекбокс правил, «нужен уровень». */
export function Toggle({ checked, onChange, label, hint, disabled }: ToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="flex w-full items-center gap-3 py-2 text-left disabled:opacity-50"
    >
      <span
        className={cn(
          'relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors',
          checked ? 'bg-accent' : 'bg-surface-2',
        )}
        aria-hidden="true"
      >
        <span
          className={cn(
            'absolute top-1 h-5 w-5 rounded-full bg-white transition-transform',
            checked ? 'translate-x-6' : 'translate-x-1',
          )}
        />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-base font-medium">{label}</span>
        {hint ? <span className="text-muted block text-sm">{hint}</span> : null}
      </span>
    </button>
  );
}

export interface CheckRowProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
}

/** Чекбокс с текстом (принятие правил). */
export function CheckRow({ checked, onChange, children }: CheckRowProps) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="flex w-full items-start gap-3 py-2 text-left"
    >
      <span
        aria-hidden="true"
        className={cn(
          'mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border',
          checked ? 'border-accent bg-accent text-accent-fg' : 'border-border bg-surface-2',
        )}
      >
        {checked ? (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M2.5 7l3 3 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        ) : null}
      </span>
      <span className="text-base font-medium">{children}</span>
    </button>
  );
}
